import {
  IAdvancedFraudTrendCheck,
  IAdvancedFraudTrendPattern,
  IFraudPatternEvidenceCheck,
  IFraudPatternEvidencePattern,
  IFraudPatternEvidences,
  IVisualTextBoxes
} from 'src/ts/interfaces/listCheck';
import { listCheckCopy } from '../listCheckCopy';

export interface PatternResultLine {
  key: string;
  label: string;
  count: number | null;
  text: string;
  couldNotTell: boolean;
  support: boolean;
  found: boolean;
}

const copy = listCheckCopy.advancedFraudTrends;

const COULD_NOT_TELL: IFraudPatternEvidenceCheck['status'][] = ['abstain', 'error'];

const lineText = (check: IAdvancedFraudTrendCheck, count: number | null): string => {
  if (count === null) return check.label;
  const line = count === 1 ? check.line_one ?? check.line : check.line;
  return line ? line.replace('{count}', String(count)) : copy.countLine(check.label, count);
};

const resultLine = (
  catalog: IAdvancedFraudTrendCheck,
  found: IFraudPatternEvidenceCheck
): PatternResultLine => {
  const flags = {
    label: catalog.label,
    count: found.count,
    support: catalog.role === 'support',
    found: found.status === 'fired'
  };
  return COULD_NOT_TELL.includes(found.status)
    ? {
        key: catalog.key,
        text: `${catalog.label}: ${copy.couldNotTell}`,
        couldNotTell: true,
        ...flags
      }
    : { key: catalog.key, text: lineText(catalog, found.count), couldNotTell: false, ...flags };
};

export const evidencesFor = (
  pattern: IAdvancedFraudTrendPattern,
  evidences: IFraudPatternEvidences | null
): IFraudPatternEvidencePattern | null =>
  evidences?.patterns?.find((item) => item.pattern === pattern.key) ?? null;

export const patternResultLines = (
  pattern: IAdvancedFraudTrendPattern,
  evidences: IFraudPatternEvidences | null,
  withSupport = false
): PatternResultLine[] => {
  const found = evidencesFor(pattern, evidences);
  if (!found) return [];
  if (pattern.view === 'fallback') {
    return (found.checks ?? [])
      .filter((check) => check.role === 'rule')
      .map((check, index) =>
        resultLine({ key: check.name, label: copy.ruleLabel(index + 1), role: 'rule' }, check)
      );
  }
  return (found.checks ?? [])
    .filter((check) => check.role === 'rule' || withSupport)
    .flatMap((check) => {
      const catalog = pattern.checks.find((item) => item.key === check.name);
      return catalog ? [resultLine(catalog, check)] : [];
    });
};

export interface HeaderPiece {
  id: number;
  text: string;
  row: number;
  bar: boolean;
  edited: boolean;
  copiesAs: string | null;
}

export interface HeaderLine {
  page: number;
  pieces: HeaderPiece[];
}

export interface SeparatorBar {
  page: number;
  copiesAs: string;
  edited: boolean;
}

const HEADER_BAR = 'header_bar';
const HEADER_SEPARATOR = 'header_separator';
const ROW_TOLERANCE = 2;

const rowOf = (bottoms: number[], bottom: number): number =>
  bottoms.findIndex((row) => Math.abs(row - bottom) <= ROW_TOLERANCE);

export const headerLines = (visual: IVisualTextBoxes | null, expectedBar: string): HeaderLine[] =>
  (visual?.pages ?? []).flatMap((page) => {
    const bars = new Map<number, string | null>();
    (page.signals ?? [])
      .filter((signal) => signal.type === HEADER_BAR)
      .forEach((signal) =>
        (signal.text_ids ?? []).forEach((id) => bars.set(id, signal.copies_as ?? null))
      );
    const text = page.text ?? [];
    const named = text.filter((piece) => bars.has(piece.id));
    if (!named.length) return [];
    const bottoms = Array.from(new Set(named.map((piece) => piece.bbox[3]))).sort((a, b) => a - b);
    const pieces = text
      .map((piece) => ({ piece, row: rowOf(bottoms, piece.bbox[3]) }))
      .filter(({ row }) => row !== -1)
      .sort((a, b) => a.row - b.row || a.piece.bbox[0] - b.piece.bbox[0])
      .map(({ piece, row }) => {
        const copiesAs = bars.get(piece.id) ?? null;
        return {
          id: piece.id,
          text: piece.text,
          row,
          bar: bars.has(piece.id),
          edited: copiesAs !== null && copiesAs !== expectedBar,
          copiesAs
        };
      });
    return [{ page: page.number, pieces }];
  });

export const separatorBars = (
  visual: IVisualTextBoxes | null,
  expectedBar: string
): SeparatorBar[] =>
  (visual?.pages ?? []).flatMap((page) =>
    (page.signals ?? [])
      .filter((signal) => signal.type === HEADER_SEPARATOR)
      .map((signal) => {
        const copiesAs = signal.copies_as ?? expectedBar;
        return { page: page.number, copiesAs, edited: copiesAs !== expectedBar };
      })
  );

export const headerSummary = (lines: HeaderLine[], expectedBar: string): string => {
  const edited = lines.filter((line) => line.pieces.some((piece) => piece.edited));
  const pages = lines.length;
  if (!edited.length) return copy.headerBarsAsExpected(expectedBar, pages);
  const actual = edited[0].pieces.find((piece) => piece.edited)?.copiesAs ?? '';
  return copy.headerBarsEdited(actual, edited.length, pages, expectedBar);
};
